import {
	AfterViewInit,
	ApplicationRef,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MEAL_PLANNING_PATH, MEAL_PLANNING_REVIEW_PLAN_PATH } from '@woolworthsnz/shop';
import { fromEvent, Observable, Subject } from 'rxjs';
import { ButtonComponent, resizeObserver, SvgIconComponent } from '@woolworthsnz/styleguide';
import { Stamp } from '../../models/stamp.interface';
import { MealPlanShortlistState, MealPlanShortlistStore } from './meal-plan-shortlist.store';
import { map, takeUntil } from 'rxjs/operators';
import { MealPlanShortlistMealComponent } from '../meal-plan-shortlist-meal/meal-plan-shortlist-meal.component';
import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';

@Component({
	selector: 'recipe-meal-plan-shortlist',
	templateUrl: './meal-plan-shortlist.component.html',
	styleUrls: ['./meal-plan-shortlist.component.scss'],
	providers: [MealPlanShortlistStore],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, ButtonComponent, SvgIconComponent, NgClass, NgFor, MealPlanShortlistMealComponent, AsyncPipe],
})
export class MealPlanShortlistComponent implements AfterViewInit, OnDestroy {
	@Input() public mealList: Stamp[];
	@Input() public totalPrice: string;

	@Output() public remove = new EventEmitter<Stamp>();
	@Output() public toggleShowDetails = new EventEmitter<boolean>();

	@ViewChild('shortlistContainer') private shortlistContainer: ElementRef;
	@ViewChild('shortlist') private shortlist: ElementRef;
	private destroyed$: Subject<boolean> = new Subject();

	public state$: Observable<MealPlanShortlistState> = this.store.selectState$;

	private originalAppPaddingBottom: string;

	constructor(private store: MealPlanShortlistStore, private router: Router, private appRef: ApplicationRef) {}

	public ngAfterViewInit(): void {
		this.originalAppPaddingBottom = this.appRef.components[0].location.nativeElement.style.paddingBottom;
		this.handleShortlistContainerResize();
		this.handleShortlistScrolling();
		this.handleExpanding();
	}

	public ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.setApplicationPaddingBottom(this.originalAppPaddingBottom);
	}

	public reviewPlan(): void {
		this.router.navigate([MEAL_PLANNING_PATH, MEAL_PLANNING_REVIEW_PLAN_PATH]);
	}

	// Can most likely be changed to meal.recipe.id when apis work.
	public removeMeal(meal: Stamp): void {
		if (this.mealList.length === 1) {
			this.toggleDetails(false);
		}

		this.remove.emit(meal);
	}

	public toggleDetails(show: boolean): void {
		this.store.updateShowDetails(show);
	}

	private handleShortlistContainerResize(): void {
		resizeObserver(this.shortlistContainer.nativeElement)
			.pipe(
				takeUntil(this.destroyed$),
				map((entries) => entries[0]?.contentRect.height)
			)
			.subscribe((height) =>
				this.setApplicationPaddingBottom(`calc(${this.originalAppPaddingBottom || '0px'} + ${height}px)`)
			);
	}

	private setApplicationPaddingBottom(height: string): void {
		this.appRef.components[0].location.nativeElement.style.paddingBottom = height;
	}

	private handleShortlistScrolling(): void {
		fromEvent<Event>(this.shortlist.nativeElement, 'scroll')
			.pipe(
				takeUntil(this.destroyed$),
				map((event: Event) => ({
					scrollLeft: event && (event.target as HTMLUListElement)?.scrollLeft,
					element: event.target as HTMLUListElement,
				}))
			)
			.subscribe(({ scrollLeft, element }) => {
				const showOverlayLeft = scrollLeft > 0;
				const roundingMargin = 1;
				const showOverlayRight =
					element.scrollWidth - element.getBoundingClientRect().width - scrollLeft >= roundingMargin;
				this.store.updateShowOverlay({ showOverlayLeft, showOverlayRight });
			});
	}

	private handleExpanding(): void {
		this.store.selectState$.pipe(takeUntil(this.destroyed$)).subscribe(({ showDetails }) => {
			this.toggleShowDetails.emit(showDetails);
		});
	}
}
